// Busca global fixa no topo: Ctrl+K foca; resultados agrupados (demandas, peças, agentes, conhecimento, radar, parceiros).
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../api';

interface Hit {
  group: string;
  id: string;
  title: string;
  subtitle: string;
  href: string;
}

export function GlobalSearch() {
  const nav = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Hit[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);

  // Atalho Ctrl+K / Cmd+K em qualquer tela.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        input.current?.focus();
        input.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) {
      setHits([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api<Hit[]>(`/api/search?q=${encodeURIComponent(q.trim())}`)
        .then((r) => {
          setHits(r);
          setActive(0);
        })
        .catch(() => setHits([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (h: Hit) => {
    setOpen(false);
    setQ('');
    nav(h.href);
  };

  const groups = [...new Set(hits.map((h) => h.group))];
  return (
    <div className="relative w-full max-w-md">
      <input
        ref={input}
        type="search"
        role="combobox"
        aria-expanded={open && q.length >= 2}
        aria-controls="busca-resultados"
        aria-label="Buscar no sistema"
        placeholder="Buscar demandas, peças, agentes…  (Ctrl+K)"
        className="w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-1.5 text-sm focus:bg-white"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') setActive((a) => Math.min(a + 1, hits.length - 1));
          else if (e.key === 'ArrowUp') setActive((a) => Math.max(a - 1, 0));
          else if (e.key === 'Enter' && hits[active]) go(hits[active]);
          else if (e.key === 'Escape') {
            setOpen(false);
            input.current?.blur();
          }
        }}
      />
      {open && q.trim().length >= 2 && (
        <div id="busca-resultados" role="listbox" className="absolute left-0 right-0 z-40 mt-1 max-h-[70vh] overflow-y-auto rounded-md border border-slate-200 bg-white p-1 shadow-lg">
          {loading && !hits.length && <p className="p-2 text-sm text-slate-500">Buscando…</p>}
          {!loading && !hits.length && <p className="p-2 text-sm text-slate-500">Nada encontrado para “{q.trim()}”.</p>}
          {groups.map((g) => (
            <div key={g}>
              <p className="px-2 pt-2 text-[11px] font-semibold uppercase text-slate-500">{g}</p>
              {hits
                .filter((h) => h.group === g)
                .map((h) => {
                  const i = hits.indexOf(h);
                  return (
                    <button
                      key={`${g}-${h.id}`}
                      role="option"
                      aria-selected={i === active}
                      className={`block w-full rounded px-2 py-1.5 text-left text-sm ${i === active ? 'bg-brand-50' : 'hover:bg-slate-50'}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(h)}
                    >
                      <span className="block truncate font-medium text-slate-900">{h.title}</span>
                      <span className="block truncate text-xs text-slate-500">{h.subtitle}</span>
                    </button>
                  );
                })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
